import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Grow,
  Link,
  Paper,
  Popper,
  Theme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import { CSVLink } from "react-csv";
import { Data, LabelKeyObject } from "react-csv/lib/core";
import { zipFileDownload } from "../common/services/Utilities";
import { AppUrl } from "../config";
import { KeyValue, ZipFile } from "../interfaces";

interface Props {
  apiDownload?: APIDownload[];
  loading?: boolean;
  downloadLinks?: LinkDownload[];
  apiDownloadAll?: APIDownload[];
  numberOfResults?: number;
  downloadLimit?: number;
}

interface APIDownload {
  data: string | KeyValue[];
  fileName: string;
  onClick: (e: any) => void;
  id?: string;
  linkText?: string;
  headers?: LabelKeyObject[] | string[];
  additionalContent?: string[][];
}

interface LinkDownload {
  href: string;
  text: string;
  id?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  dataDownload: {
    fontSize: 14,
  },
  menu: {
    marginRight: 5,
  },
  modalDownload: {
    "position": "relative",
    "margin": "0 auto 1rem auto",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "height": 32,
    "width": 170,
    "border": "1px solid #ACC8DF",
    "borderRadius": 4,
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "backgroundColor": theme.palette.secondary.main,
    "&:hover, &:focus-visible, &:focus": {
      textDecoration: "none",
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
    },
  },
  CSVLink: {
    "fontSize": "1rem",
    "fontWeight": 400,
    "lineHeight": 1.5,
    "width": "100%",
    "color": theme.palette.primary.dark,
    "display": "block",
    "padding": "6px 16px",
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "none",
    },
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "none",
    },
    "&:active": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      textDecoration: "none",
    },
  },
  downloadButton: {
    "right": 0,
    "margin": 0,
    "bottom": 0,
    "height": 32,
    "width": 160,
    "position": "absolute",
    "minWidth": 40,
    "textAlign": "center",
    "& svg": {
      color: theme.palette.primary.main,
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      left: 0,
      right: 0,
      margin: "auto",
      textAlign: "center",
    },
    "& .MuiButton-label": {
      "width": 40,
      "& .MuiCircularProgress-root": {
        position: "absolute",
      },
    },
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
    "&:focus": {
      backgroundColor: "#e0e0e0",
    },
    "&:active": {
      backgroundColor: "#c7c7c7",
    },
  },
  dialog: {
    '& div[role="dialog"]': {
      padding: "32px 32px 8px 32px",
      maxWidth: "500px",
    },
    "& .MuiDialogContentText-root": {
      marginBottom: 0,
      paddingBottom: 0,
    },
    "& .MuiDialogContent-root": {
      padding: 0,
    },
  },
  dialogButton: {
    "width": 170,
    "display": "flex",
    "&#cancel-download, &#continue-download": {
      "margin": "0 auto 1rem auto",
      "&:hover, &:focus": {
        backgroundColor: theme.palette.primary.main,
        color: "#ffffff",
      },
      "&:active": {
        backgroundColor: "#ffffff",
        color: theme.palette.primary.main,
      },
    },
  },

  dialogTitle: {
    "textAlign": "center",
    "& > h2": {
      color: "#1A4480",
      fontSize: 24,
    },
  },
  dialogContent: {
    padding: 0,
  },
}));

export default function DataDownload({
  apiDownload,
  loading,
  downloadLinks,
  numberOfResults,
  downloadLimit,
}: Props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [downloading, setDownloading] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [downloadIndex, setDownloadIndex] = React.useState<number>(0);

  React.useEffect(() => {
    if (open && openModal) {
      if (loading && !downloading) {
        setDownloading(true);
      }
      if (downloading && !loading) {
        setDownloading(false);
        setOpen(false);
        setOpenModal(false);
      }
    }
  }, [downloading, loading, open, openModal]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenModal(false);
  };

  function getData(e: any, index: number) {
    e.preventDefault();
    setDownloadIndex(index);
    setOpenModal(true);
  }

  const handleClick = () => {
    const stringArray: ZipFile[] = [
      {
        url: `${AppUrl("API")}/institutions_definitions.csv`,
        name: "institutions_definitions",
      },
      {
        url: `${AppUrl("API")}/events_definitions.csv`,
        name: "events_definitions",
      },
      {
        url: `${AppUrl("API")}/locations_definitions.csv`,
        name: "locations_definitions",
      },
    ];
    zipFileDownload(stringArray, "variable_definitions");
    handleClose();
  };

  return (
    <>
      <Button
        ref={anchorRef}
        id="dataDownload"
        aria-label="Download Options"
        aria-controls={open ? "dataDownloadMenu" : undefined}
        aria-haspopup="true"
        className={classes.downloadButton}
        onClick={handleToggle}
        variant="contained"
        color="secondary"
      >
        {loading && <CircularProgress disableShrink />}
        Download Results
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="left"
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 10 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className={classes.menu}>
              <ClickAwayListener onClickAway={handleClose}>
                <div id="dataDownloadMenu">
                  {apiDownload
                    ? apiDownload.map((data, index) => {
                        const downloadData =
                          downloadIndex && openModal
                            ? (apiDownload[downloadIndex - 1].data as Data)
                            : (data.data as Data);

                        let filteredData: any = downloadData.filter(
                          (responseData: KeyValue) =>
                            responseData !== undefined,
                        );

                        const downloadModal =
                          downloadLimit &&
                          numberOfResults &&
                          numberOfResults > downloadLimit;

                        const csvDownload = (bypass?: boolean) => {
                          const downloadData: KeyValue =
                            downloadIndex && openModal
                              ? apiDownload[downloadIndex - 1]
                              : data;
                          if (downloadData.additionalContent) {
                            const headers = downloadData?.headers.map(
                              (header: KeyValue) => header.label,
                            );

                            const headerKeys = downloadData?.headers.map(
                              (header: KeyValue) => header.key,
                            );

                            const newfilteredData: any = [];

                            filteredData.forEach((data: KeyValue) => {
                              const array = headerKeys.map(
                                (key: string) => data[key],
                              );
                              // Check to see if Cert or Namefull (For Totals Row) exists
                              if (array[0] || array[2]) {
                                newfilteredData.push(array);
                              }
                            });
                            if (newfilteredData[0]) {
                              filteredData = newfilteredData;
                            }

                            if (
                              Array.isArray(filteredData[0]) &&
                              filteredData[0].toString() !==
                                downloadData.additionalContent[0].toString()
                            ) {
                              filteredData = [
                                ...downloadData.additionalContent,

                                [],
                                headers,
                                ...filteredData,
                              ];
                            }
                          }
                          return (
                            <CSVLink
                              className={
                                bypass ? classes.modalDownload : classes.CSVLink
                              }
                              id={
                                bypass ? "continue-download" : downloadData.id
                              }
                              headers={
                                downloadData.additionalContent
                                  ? undefined
                                  : downloadData.headers
                              }
                              filename={downloadData.fileName}
                              data={filteredData}
                              asyncOnClick={true}
                              onClick={(e: any) => {
                                if (!downloadModal || bypass) {
                                  downloadData.onClick(e);
                                } else {
                                  getData(e, index + 1);
                                }
                              }}
                            >
                              {bypass
                                ? "Continue"
                                : data.linkText
                                ? data.linkText
                                : "Download Results Data"}
                              {bypass && loading && (
                                <CircularProgress
                                  style={{ position: "absolute", right: 14 }}
                                  disableShrink
                                  size={20}
                                  color="secondary"
                                />
                              )}
                            </CSVLink>
                          );
                        };

                        const largeDownloadAlert = (
                          <Dialog
                            id="large-download-alert"
                            className={classes.dialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            open={openModal}
                          >
                            <DialogContent>
                              <DialogContentText
                                variant="body2"
                                color="primary"
                                id="alert-dialog-description"
                              >
                                You have requested a large data download of over{" "}
                                {downloadLimit && downloadLimit > 999
                                  ? `${downloadLimit / 1000}k`
                                  : downloadLimit}{" "}
                                records. This will take longer to complete than
                                usual. Please be patient while we process your
                                request.
                              </DialogContentText>
                              <DialogTitle
                                className={classes.dialogTitle}
                                id="alert-dialog-title"
                              >
                                Do you wish to continue?
                              </DialogTitle>
                            </DialogContent>
                            <DialogActions>
                              <Grid container spacing={2}>
                                <Grid item sm={6} xs={12}>
                                  <Button
                                    className={classes.dialogButton}
                                    onClick={handleClose}
                                    color="secondary"
                                    variant="contained"
                                    id="cancel-download"
                                  >
                                    Cancel
                                  </Button>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                  {csvDownload(true)}
                                </Grid>
                              </Grid>
                            </DialogActions>
                          </Dialog>
                        );

                        return (
                          <React.Fragment key={index}>
                            {downloadModal && openModal && largeDownloadAlert}
                            {csvDownload()}
                          </React.Fragment>
                        );
                      })
                    : undefined}

                  {downloadLinks
                    ? downloadLinks.map((link: LinkDownload, int: number) => {
                        return (
                          <div key={int}>
                            {window.location.pathname.includes("details") ? (
                              <Button
                                style={{
                                  margin: 0,
                                  lineHeight: 0,
                                  borderRadius: 0,
                                }}
                                key={int}
                                className={classes.CSVLink}
                                onClick={handleClick}
                              >
                                {link.text}
                              </Button>
                            ) : (
                              <Link
                                key={int}
                                id={link.id}
                                href={link.href}
                                className={classes.CSVLink}
                                onClick={handleClose}
                              >
                                {link.text}
                              </Link>
                            )}
                          </div>
                        );
                      })
                    : undefined}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
